import React from "react";
import { Info, Clock, ShieldCheck, Trophy, Star, ChevronRight, CheckCircle2, XCircle, Medal } from "lucide-react";

export default function RulesPage() {
  const scoringRules = [
    {
      title: "Tendencia (Ganador o Empate)",
      desc: "Si aciertas quién gana el partido o si termina en empate.",
      points1: "5",
      points2: "10",
      icon: <Trophy className="text-yellow-500" />,
    },
    {
      title: "Goles Equipo Local",
      desc: "Por acertar la cifra exacta de goles del local.",
      points1: "2",
      points2: "4",
      icon: <Star className="text-blue-400" />,
    },
    {
      title: "Goles Equipo Visitante",
      desc: "Por acertar la cifra exacta de goles del visitante.",
      points1: "2",
      points2: "4",
      icon: <Star className="text-blue-400" />,
    },
    {
      title: "Diferencia de Goles",
      desc: "Si aciertas por cuántos goles de diferencia se ganó.",
      points1: "1",
      points2: "2",
      icon: <ShieldCheck className="text-green-400" />,
    },
  ];

  // NUEVA CONSTANTE PARA REGLAS DE PODIO
  const podiumRules = [
    { pos: "🥇 Campeón", pts: "40", color: "bg-yellow-400", text: "text-yellow-700" },
    { pos: "🥈 Subcampeón", pts: "20", color: "bg-slate-300", text: "text-slate-600" },
    { pos: "🥉 Tercer Puesto", pts: "10", color: "bg-orange-300", text: "text-orange-700" },
    { pos: "🏅 Cuarto Puesto", pts: "10", color: "bg-blue-300", text: "text-blue-700" },
  ];

  const examples = [
    {
      title: "Puntaje Pleno (Exacto)",
      user: "1 - 0",
      real: "1 - 0",
      points: "10",
      fase: "Fase de Grupos",
      breakdown: ["Tendencia: +5", "Local: +2", "Visitante: +2", "Diferencia: +1"]
    },
    {
      title: "Acierto Parcial (Diferencia)",
      user: "2 - 0",
      real: "3 - 1",
      points: "6",
      fase: "Fase de Grupos",
      breakdown: ["Tendencia: +5", "Diferencia: +1", "Goles Local: 0", "Goles Visitante: 0"]
    },
    {
      title: "Acierto Goles (Sin Tendencia)",
      user: "1 - 2",
      real: "3 - 2",
      points: "2",
      fase: "Fase de Grupos",
      breakdown: ["Goles Visitante: +2", "Tendencia: 0", "Diferencia: 0", "Goles Local: 0"]
    },
    {
      title: "Fases Finales (Doble Puntos)",
      user: "1 - 1",
      real: "1 - 1",
      points: "20",
      fase: "Fases Finales",
      breakdown: ["Tendencia: +10", "Local: +4", "Visitante: +4", "Diferencia: +2"]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        
        {/* HEADER TIPO DASHBOARD */}
        <div className="bg-[#0052cc] rounded-3xl p-8 mb-8 shadow-2xl relative overflow-hidden text-white border-b-8 border-blue-800">
          <div className="relative z-10 text-center md:text-left">
            <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-2 italic">
              Reglamento Oficial <span className="text-yellow-400">Golístico</span>
            </h1>
            <p className="text-blue-100 max-w-2xl font-medium">
              Conoce las reglas del juego. Cada gol cuenta, cada predicción te acerca a la gloria.
            </p>
          </div>
          <div className="absolute right-[-10%] top-[-20%] w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        </div>

        {/* INFO RÁPIDA */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200 flex flex-col items-center text-center">
            <Clock className="text-orange-500 mb-3" size={32} />
            <h3 className="font-black uppercase text-[12px] tracking-widest mb-1 text-slate-400">Cierre de Polla</h3>
            <p className="text-sm text-slate-700 font-bold leading-tight">Hasta <span className="text-orange-600 font-black">10 min antes</span> del partido.</p>
          </div>
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200 flex flex-col items-center text-center">
            <ShieldCheck className="text-blue-600 mb-3" size={32} />
            <h3 className="font-black uppercase text-[12px] tracking-widest mb-1 text-slate-400">Tiempo de Juego</h3>
            <p className="text-sm text-slate-700 font-bold leading-tight italic">Incluye Prórroga. <span className="text-blue-600 font-black">No incluye penaltis.</span></p>
          </div>
          <div className="bg-[#0052cc] p-6 rounded-3xl shadow-lg flex flex-col items-center text-center text-white">
            <Trophy className="text-yellow-400 mb-3" size={32} />
            <div className="flex flex-col">
              <span className="text-lg font-black italic tracking-tighter leading-none text-yellow-400 uppercase">Grupos: 10 pts máx</span>
              <div className="h-px bg-white/20 my-2"></div>
              <span className="text-lg font-black italic tracking-tighter leading-none uppercase">Finales: 20 pts máx</span>
            </div>
          </div>
        </div>

        {/* SISTEMA DE PUNTOS */}
        <h2 className="text-xl font-black uppercase tracking-widest mb-6 flex items-center gap-3 text-slate-800">
          <div className="w-2 h-8 bg-blue-600 rounded-full"></div>
          Sistema de Puntuación
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          {scoringRules.map((rule, idx) => (
            <div key={idx} className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center shrink-0 border border-slate-100">{rule.icon}</div>
              <div className="flex-1">
                <h4 className="font-black text-[13px] uppercase text-slate-800 leading-tight mb-1">{rule.title}</h4>
                <p className="text-[11px] text-slate-500 font-medium leading-tight">{rule.desc}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0 min-w-[110px]">
                <div className="flex items-center justify-between bg-slate-100 px-2 py-1.5 rounded-lg border border-slate-200">
                  <span className="text-[7px] font-black text-slate-400 uppercase">Grupos</span>
                  <span className="text-xs font-black text-slate-700 ml-1">+{rule.points1}</span>
                </div>
                <div className="flex items-center justify-between bg-blue-600 px-2 py-1.5 rounded-lg shadow-sm">
                  <span className="text-[7px] font-black text-blue-100 uppercase">Finales</span>
                  <span className="text-xs font-black text-white ml-1">+{rule.points2}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* NUEVA SECCIÓN: PUNTOS POR PODIO FINAL */}
        <h2 className="text-xl font-black uppercase tracking-widest mb-6 flex items-center gap-3 text-slate-800">
          <div className="w-2 h-8 bg-yellow-400 rounded-full"></div>
          Podio Final (Cuadro de Honor)
        </h2>
        <div className="bg-white rounded-[40px] p-8 border border-slate-200 shadow-sm mb-16 relative overflow-hidden">
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {podiumRules.map((p, i) => (
              <div key={i} className="flex flex-col items-center p-4 rounded-3xl bg-slate-50 border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase mb-3">{p.pos}</span>
                <div className={`${p.color} w-16 h-16 rounded-full flex items-center justify-center shadow-lg mb-3 border-4 border-white`}>
                   <Medal className={p.text} size={30} />
                </div>
                <span className={`text-2xl font-black ${p.text.replace('text-', 'text-')}`}>+{p.pts} <span className="text-xs">PTS</span></span>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-[11px] font-bold text-slate-400 uppercase italic">
            * Estos puntos se asignan una vez finalizado el torneo al acertar la posición exacta.
          </p>
        </div>

        {/* SECCIÓN DE EJEMPLOS AMPLIADA */}
        <h2 className="text-xl font-black uppercase tracking-widest mb-6 flex items-center gap-3 text-slate-800">
          <div className="w-2 h-8 bg-green-500 rounded-full"></div>
          Ejemplos Prácticos
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {examples.map((ex, idx) => (
            <div key={idx} className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              <div className="bg-slate-50 p-4 border-b border-slate-100 flex justify-between items-center px-6">
                <span className="font-black uppercase text-[11px] tracking-wider text-slate-500">{ex.title}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ex.fase === 'Fases Finales' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>{ex.fase}</span>
              </div>
              
              <div className="p-6 flex-1">
                <div className="flex justify-between items-center mb-6">
                  <div className="text-center">
                    <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Tu Pronóstico</p>
                    <div className="bg-blue-600 text-white font-black text-xl px-4 py-2 rounded-xl shadow-md border-b-4 border-blue-800">{ex.user}</div>
                  </div>
                  <ChevronRight className="text-slate-300" size={24} />
                  <div className="text-center">
                    <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Resultado Final</p>
                    <div className="bg-slate-800 text-white font-black text-xl px-4 py-2 rounded-xl shadow-md border-b-4 border-slate-900">{ex.real}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-1">Desglose de Puntos</p>
                  {ex.breakdown.map((item, i) => (
                    <div key={i} className="flex justify-between items-center text-xs">
                      <span className="text-slate-600 font-medium flex items-center gap-2">
                        {item.includes("+0") || item.includes(": 0") ? <XCircle size={12} className="text-slate-300" /> : <CheckCircle2 size={12} className="text-green-500" />}
                        {item.split(":")[0]}
                      </span>
                      <span className={`font-black ${item.includes("+0") || item.includes(": 0") ? 'text-slate-300' : 'text-slate-800'}`}>{item.split(":")[1]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-900 p-4 flex justify-between items-center px-8">
                <span className="text-white/60 font-black text-[10px] uppercase">Puntaje Obtenido</span>
                <span className="text-white font-black text-2xl italic tracking-tighter">{ex.points} <span className="text-xs not-italic text-yellow-400">PTS</span></span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-blue-50 border-2 border-blue-100 rounded-3xl p-6 text-center">
          <p className="text-sm text-blue-900 font-medium italic">
            "Recuerda que los puntos son acumulables. Si aciertas el resultado exacto, sumas la tendencia, los goles de ambos equipos y la diferencia."
          </p>
        </div>

      </div>
    </div>
  );
}